import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getApiRequest } from "../../services/getApiRequest";
import { postApiRequest } from "../../services/postApiRequest";
import { END_POINTS } from "../../Helper/Constant";

const TYPE_TO_SECTION = {
  coin_package: "coins",
  card_skin: "cards",
  table_background: "tables",
};

const SECTION_KIND = {
  coins: "coin",
  cards: "card",
  tables: "table",
};

const formatMoneyPrice = (value, currency = "SAR") => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) return `0 ${currency}`;
  const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${formatted} ${currency}`;
};

const mapPlayerStoreItem = (item, kind = "card") => {
  const isCoin = kind === "coin";

  return {
    id: item._id,
    name: item.name,
    image: item.previewUrl || item.assetUrl || "",
    price: isCoin
      ? formatMoneyPrice(item.realMoneyPrice, item.currency || "SAR")
      : item.price,
    // Cost in coins (for coin packs `price` above shows the SAR price instead)
    coinPrice: item.price,
    coinAmount: item.coinAmount,
    isPurchased: Boolean(item.isPurchased),
    isEquipped: Boolean(item.isEquipped),
    type: item.type,
    sortOrder: item.sortOrder ?? 0,
    badge: null,
  };
};

const normalizePlayerStoreItems = (items, kind) =>
  (items || [])
    .filter((item) => item?.isActive !== false)
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((item) => mapPlayerStoreItem(item, kind));

export const fetchStoreItemsByType = createAsyncThunk(
  "playerStore/fetchByType",
  async ({ type, lang, userId, gameTypeId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        type,
        lang: lang === "ar" ? "ar" : "en",
      });
      if (userId) params.set("userId", userId);
      if (gameTypeId) params.set("gameTypeId", gameTypeId);

      const res = await getApiRequest(
        `${END_POINTS.GET_STORE_ITEMS}?${params.toString()}`
      );

      if (res?.success) {
        return { type, items: res.items || [] };
      }

      return rejectWithValue({
        type,
        message: res?.message || "Failed to load store items",
      });
    } catch (error) {
      return rejectWithValue({
        type,
        message: error?.response?.data?.message || error.message,
      });
    }
  }
);

export const purchaseStoreItem = createAsyncThunk(
  "playerStore/purchase",
  async (itemId, { rejectWithValue }) => {
    try {
      const res = await postApiRequest(
        `${END_POINTS.GET_STORE_ITEMS}/${itemId}/purchase`,
        {}
      );

      if (res?.success) {
        return { itemId, message: res?.message };
      }

      return rejectWithValue(
        res?.message || "Failed to purchase store item"
      );
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message
      );
    }
  }
);

export const equipStoreItem = createAsyncThunk(
  "playerStore/equip",
  async (itemId, { rejectWithValue }) => {
    try {
      const res = await postApiRequest(
        `${END_POINTS.GET_STORE_ITEMS}/${itemId}/equip`,
        {}
      );

      if (res?.success) {
        return { itemId, message: res?.message };
      }

      return rejectWithValue(res?.message || "Failed to equip store item");
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error.message
      );
    }
  }
);

// Only one item per section (card / table) can be equipped at a time
const setEquippedInSection = (state, itemId) => {
  ["cards", "tables"].forEach((section) => {
    if (!state[section].some((item) => item.id === itemId)) return;
    state[section] = state[section].map((item) => ({
      ...item,
      isEquipped: item.id === itemId,
    }));
  });
};

const playerStoreSlice = createSlice({
  name: "playerStore",
  initialState: {
    coins: [],
    cards: [],
    tables: [],
    loading: { coins: false, cards: false, tables: false },
    error: { coins: null, cards: null, tables: null },
    purchasingId: null,
    equippingId: null,
  },
  reducers: {
    markStoreItemPurchased: (state, action) => {
      const itemId = action.payload;
      ["cards", "tables"].forEach((section) => {
        state[section] = state[section].map((item) =>
          item.id === itemId ? { ...item, isPurchased: true } : item
        );
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoreItemsByType.pending, (state, action) => {
        const section = TYPE_TO_SECTION[action.meta.arg.type];
        if (!section) return;
        state.loading[section] = true;
        state.error[section] = null;
      })
      .addCase(fetchStoreItemsByType.fulfilled, (state, action) => {
        const section = TYPE_TO_SECTION[action.payload.type];
        if (!section) return;
        state.loading[section] = false;
        state[section] = normalizePlayerStoreItems(
          action.payload.items,
          SECTION_KIND[section]
        );
      })
      .addCase(fetchStoreItemsByType.rejected, (state, action) => {
        const section = TYPE_TO_SECTION[action.meta.arg.type];
        if (!section) return;
        state.loading[section] = false;
        state[section] = [];
        state.error[section] = action.payload?.message || "Failed to load";
      })
      .addCase(purchaseStoreItem.pending, (state, action) => {
        state.purchasingId = action.meta.arg;
      })
      .addCase(purchaseStoreItem.fulfilled, (state, action) => {
        const itemId = action.payload.itemId;
        state.purchasingId = null;
        ["cards", "tables"].forEach((section) => {
          state[section] = state[section].map((item) =>
            item.id === itemId ? { ...item, isPurchased: true } : item
          );
        });
      })
      .addCase(purchaseStoreItem.rejected, (state) => {
        state.purchasingId = null;
      })
      .addCase(equipStoreItem.pending, (state, action) => {
        state.equippingId = action.meta.arg;
      })
      .addCase(equipStoreItem.fulfilled, (state, action) => {
        state.equippingId = null;
        setEquippedInSection(state, action.payload.itemId);
      })
      .addCase(equipStoreItem.rejected, (state) => {
        state.equippingId = null;
      });
  },
});

export const { markStoreItemPurchased } = playerStoreSlice.actions;
export default playerStoreSlice.reducer;
